import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { replicaService } from '../lib/services';
import { VoiceTone } from '../types';
import './ReplicaCreationRitual.css';

/* ================================================================
   ReplicaCreationRitual — Immersive 5-screen emotional onboarding
   ================================================================ */

interface ReplicaCreationRitualProps {
    /** Called after birth — receives the replica ID so the caller can navigate to the edit form */
    onComplete: (replicaId?: string) => void;
    onCancel?: () => void;
}

// ── Silhouette vertices & edges (simplified luminous humanoid outline) ──
const SILHOUETTE_VERTS: [number, number][] = [
    // Head outline
    [0.50, 0.08], [0.38, 0.10], [0.30, 0.16], [0.26, 0.26], [0.28, 0.36],
    [0.34, 0.42], [0.40, 0.46], [0.50, 0.48], [0.60, 0.46], [0.66, 0.42],
    [0.72, 0.36], [0.74, 0.26], [0.70, 0.16], [0.62, 0.10],
    // Neck
    [0.43, 0.48], [0.57, 0.48], [0.42, 0.56], [0.58, 0.56],
    // Shoulders
    [0.22, 0.62], [0.78, 0.62], [0.18, 0.68], [0.82, 0.68],
    // Torso
    [0.24, 0.82], [0.76, 0.82], [0.28, 0.95], [0.72, 0.95],
    // Eyes (left)
    [0.38, 0.26], [0.42, 0.24], [0.46, 0.26], [0.42, 0.28],
    // Eyes (right)
    [0.54, 0.26], [0.58, 0.24], [0.62, 0.26], [0.58, 0.28],
    // Mouth
    [0.42, 0.38], [0.46, 0.40], [0.50, 0.41], [0.54, 0.40], [0.58, 0.38],
];

const SILHOUETTE_EDGES: [number, number][] = [
    // Head outline
    [0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 7], [7, 8], [8, 9], [9, 10], [10, 11], [11, 12], [12, 13], [13, 0],
    // Neck
    [14, 16], [15, 17], [6, 14], [8, 15],
    // Shoulders
    [16, 18], [17, 19], [18, 20], [19, 21],
    // Torso
    [20, 22], [21, 23], [22, 24], [23, 25],
    // Left eye
    [26, 27], [27, 28], [28, 29], [29, 26],
    // Right eye
    [30, 31], [31, 32], [32, 33], [33, 30],
    // Mouth
    [34, 35], [35, 36], [36, 37], [37, 38],
];

const VOICE_TONE_IDS: VoiceTone[] = ['professional', 'friendly', 'warm', 'authoritative'];

const NAME_SUGGESTIONS = ['Nova', 'Ari', 'Lux', 'Echo', 'Sage'];

const ReplicaCreationRitual: React.FC<ReplicaCreationRitualProps> = ({ onComplete, onCancel }) => {
    const { user } = useAuth();
    const { t } = useTranslation('dashboard');

    // ── State ──
    const [currentStep, setCurrentStep] = useState(0); // 0-4
    const [stepState, setStepState] = useState<'active' | 'exiting'>('active');
    const [replicaName, setReplicaName] = useState('');
    const [seedPhrase, setSeedPhrase] = useState('');
    const [voiceTone, setVoiceTone] = useState<VoiceTone>('professional');
    const [isCreating, setIsCreating] = useState(false);
    const [showBirthFlash, setShowBirthFlash] = useState(false);
    const [isBorn, setIsBorn] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [existingReplicaId, setExistingReplicaId] = useState<string | null>(null);
    const [createdReplicaId, setCreatedReplicaId] = useState<string | null>(null);

    // ── Refs ──
    const particleCanvasRef = useRef<HTMLCanvasElement>(null);
    const silhouetteCanvasRef = useRef<HTMLCanvasElement>(null);
    const animFrameRef = useRef<number>(0);
    const timeRef = useRef(0);

    // ── Check for existing replica on mount ──
    useEffect(() => {
        if (!user?.uid) return;
        replicaService.getUserReplicas(user.uid).then(response => {
            if (response.data?.replicas?.length) {
                // Only pre-fill from an active (non-killed) replica
                const active = response.data.replicas.find(
                    (r: any) => r.operationalStatus !== 'KILLED'
                );
                if (active) {
                    setExistingReplicaId(active.id);
                    if (active.name) setReplicaName(active.name);
                    if (active.personality) setSeedPhrase(active.personality);
                    if (active.voiceTone) setVoiceTone(active.voiceTone as VoiceTone);
                }
                // If only killed replicas exist, treat as fresh creation
            }
        }).catch(() => { /* First-time user — no replicas yet */ });
    }, [user?.uid]);

    // ── Step navigation ──
    const goToStep = useCallback((next: number) => {
        setStepState('exiting');
        setTimeout(() => {
            setCurrentStep(next);
            setStepState('active');
        }, 400);
    }, []);

    const handleNext = useCallback(() => {
        if (currentStep < 4) {
            goToStep(currentStep + 1);
        }
    }, [currentStep, goToStep]);

    // ── Birth sequence ──
    const triggerBirth = useCallback(async () => {
        if (!user?.uid) return;

        setIsCreating(true);
        setError(null);

        try {
            let replicaId: string;

            if (existingReplicaId) {
                // ── Update existing replica with ritual data ──
                const updatePayload = {
                    name: replicaName || undefined,
                    voiceTone,
                    personalityDescription: seedPhrase || undefined,
                };
                const response = await replicaService.update(existingReplicaId, updatePayload);
                if (response.error) throw new Error(response.error);
                replicaId = existingReplicaId;
            } else {
                // ── Create a new replica ──
                const payload = {
                    name: replicaName || 'My Replica',
                    category: 'General',
                    description: seedPhrase || 'Born from ritual creation',
                    ownerId: user.uid,
                    voiceTone,
                    voiceSpeed: 1.0,
                    language: 'English',
                    responseSpeed: 'fast' as string,
                    personalityTraits: [] as string[],
                    personalityDescription: seedPhrase,
                    deploymentOption: 'website-widget',
                    guardianAssignment: 'self',
                };

                const response = await replicaService.create(payload);

                // Handle "already has a replica" — fetch and update instead
                if (response.error && response.error.includes('limited to one Replica')) {
                    const listResp = await replicaService.getUserReplicas(user.uid);
                    if (listResp.data?.replicas?.length) {
                        const existing = listResp.data.replicas[0];
                        setExistingReplicaId(existing.id);
                        const upResp = await replicaService.update(existing.id, {
                            name: replicaName || undefined,
                            voiceTone,
                            personalityDescription: seedPhrase || undefined,
                        });
                        if (upResp.error) throw new Error(upResp.error);
                        replicaId = existing.id;
                    } else {
                        throw new Error(response.error);
                    }
                } else if (response.error) {
                    throw new Error(response.error);
                } else {
                    replicaId = response.data!.replica.id;
                }
            }

            setCreatedReplicaId(replicaId);

            // Show birth animation
            setShowBirthFlash(true);
            setTimeout(() => {
                setIsBorn(true);
                setIsCreating(false);
            }, 1200);
        } catch (err) {
            console.error('Failed to create replica:', err);
            setError(err instanceof Error ? err.message : 'Creation failed');
            setIsCreating(false);
        }
    }, [user, replicaName, seedPhrase, voiceTone, existingReplicaId]);

    // ── Particle system ──
    useEffect(() => {
        const canvas = particleCanvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        interface Particle {
            x: number; y: number;
            vx: number; vy: number;
            size: number;
            alpha: number;
            color: [number, number, number];
        }

        const particles: Particle[] = [];
        const PARTICLE_COUNT = 60;

        const resize = () => {
            const dpr = window.devicePixelRatio || 1;
            canvas.width = window.innerWidth * dpr;
            canvas.height = window.innerHeight * dpr;
            canvas.style.width = `${window.innerWidth}px`;
            canvas.style.height = `${window.innerHeight}px`;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };

        const init = () => {
            particles.length = 0;
            for (let i = 0; i < PARTICLE_COUNT; i++) {
                particles.push({
                    x: Math.random() * window.innerWidth,
                    y: Math.random() * window.innerHeight,
                    vx: (Math.random() - 0.5) * 0.3,
                    vy: (Math.random() - 0.5) * 0.3,
                    size: 1 + Math.random() * 2.5,
                    alpha: 0.1 + Math.random() * 0.4,
                    color: Math.random() > 0.5
                        ? [207, 166, 96]       // Gold
                        : [140, 130, 200],     // Violet
                });
            }
        };

        const animate = () => {
            const w = window.innerWidth;
            const h = window.innerHeight;
            ctx.clearRect(0, 0, w, h);
            timeRef.current += 0.01;

            for (const p of particles) {
                p.x += p.vx + Math.sin(timeRef.current + p.y * 0.005) * 0.15;
                p.y += p.vy + Math.cos(timeRef.current + p.x * 0.005) * 0.1;

                // Wrap
                if (p.x < -10) p.x = w + 10;
                if (p.x > w + 10) p.x = -10;
                if (p.y < -10) p.y = h + 10;
                if (p.y > h + 10) p.y = -10;

                const breathe = 0.6 + 0.4 * Math.sin(timeRef.current * 1.2 + p.x * 0.01);
                const a = p.alpha * breathe;

                // Glow
                const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 6);
                grad.addColorStop(0, `rgba(${p.color[0]}, ${p.color[1]}, ${p.color[2]}, ${a * 0.3})`);
                grad.addColorStop(1, `rgba(${p.color[0]}, ${p.color[1]}, ${p.color[2]}, 0)`);
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.size * 6, 0, Math.PI * 2);
                ctx.fillStyle = grad;
                ctx.fill();

                // Core
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(${p.color[0]}, ${p.color[1]}, ${p.color[2]}, ${a})`;
                ctx.fill();
            }

            animFrameRef.current = requestAnimationFrame(animate);
        };

        resize();
        init();
        animate();
        window.addEventListener('resize', resize);

        return () => {
            window.removeEventListener('resize', resize);
            cancelAnimationFrame(animFrameRef.current);
        };
    }, []);

    // ── Silhouette renderer ──
    useEffect(() => {
        const canvas = silhouetteCanvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const dpr = window.devicePixelRatio || 1;
        const W = 180;
        const H = 220;
        canvas.width = W * dpr;
        canvas.height = H * dpr;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        const R = 207, G = 166, B = 96;

        // Draw the silhouette wireframe
        ctx.clearRect(0, 0, W, H);

        // Draw edges
        ctx.lineCap = 'round';
        for (const [i, j] of SILHOUETTE_EDGES) {
            const [x1, y1] = SILHOUETTE_VERTS[i];
            const [x2, y2] = SILHOUETTE_VERTS[j];

            ctx.beginPath();
            ctx.moveTo(x1 * W, y1 * H);
            ctx.lineTo(x2 * W, y2 * H);
            ctx.strokeStyle = `rgba(${R}, ${G}, ${B}, 0.5)`;
            ctx.lineWidth = 1.2;
            ctx.stroke();
        }

        // Draw vertices with glow
        for (const [vx, vy] of SILHOUETTE_VERTS) {
            const x = vx * W;
            const y = vy * H;

            const grad = ctx.createRadialGradient(x, y, 0, x, y, 6);
            grad.addColorStop(0, `rgba(${R}, ${G}, ${B}, 0.4)`);
            grad.addColorStop(1, `rgba(${R}, ${G}, ${B}, 0)`);
            ctx.beginPath();
            ctx.arc(x, y, 6, 0, Math.PI * 2);
            ctx.fillStyle = grad;
            ctx.fill();

            ctx.beginPath();
            ctx.arc(x, y, 1.5, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(${R}, ${G}, ${B}, 0.7)`;
            ctx.fill();
        }
    }, []);

    // ── Silhouette clarity class ──
    const getSilhouetteClass = () => {
        if (isBorn) return 'ritual-silhouette--born';
        switch (currentStep) {
            case 0: return 'ritual-silhouette--blurry';
            case 1: return 'ritual-silhouette--blurry';
            case 2: return 'ritual-silhouette--forming';
            case 3: return 'ritual-silhouette--clear';
            case 4: return isBorn ? 'ritual-silhouette--born' : 'ritual-silhouette--clear';
            default: return 'ritual-silhouette--blurry';
        }
    };

    // ── Background class ──
    const getBgClass = () => {
        switch (currentStep) {
            case 0: return 'ritual-bg--entrance';
            case 1: return 'ritual-bg--identity';
            case 2: return 'ritual-bg--seed';
            case 3: return 'ritual-bg--voice';
            case 4: return 'ritual-bg--birth';
            default: return 'ritual-bg--entrance';
        }
    };

    // ── Handle final CTA: go to detailed edit form ──
    const handleFinish = useCallback(() => {
        const id = createdReplicaId || existingReplicaId;
        onComplete(id || undefined);
    }, [createdReplicaId, existingReplicaId, onComplete]);

    // ── Render each step ──
    const renderStep = () => {
        const stepClass = `ritual-step ${stepState === 'active' ? 'ritual-step--active' : 'ritual-step--exiting'}`;

        switch (currentStep) {
            // ═══ SCREEN 1 — L'ENTRÉE ═══
            case 0:
                return (
                    <div className={stepClass} key="entrance">
                        <div className="ritual-halo" />
                        <h1 className="ritual-title" style={{ whiteSpace: 'pre-line' }}>
                            {t('ritual.entrance_title')}
                        </h1>
                        <p className="ritual-subtitle">
                            {t('ritual.entrance_subtitle')}
                        </p>
                        <button className="ritual-cta ritual-cta--primary" onClick={handleNext}>
                            {t('ritual.begin')}
                        </button>
                    </div>
                );

            // ═══ SCREEN 2 — L'IDENTITÉ ═══
            case 1:
                return (
                    <div className={stepClass} key="identity">
                        <div className={`ritual-silhouette ${getSilhouetteClass()}`}>
                            <canvas ref={silhouetteCanvasRef} />
                        </div>
                        <h1 className="ritual-title" style={{ whiteSpace: 'pre-line' }}>
                            {t('ritual.identity_title')}
                        </h1>
                        <input
                            className="ritual-input"
                            type="text"
                            placeholder={t('ritual.name_placeholder')}
                            value={replicaName}
                            onChange={(e) => setReplicaName(e.target.value)}
                            autoFocus
                            maxLength={40}
                        />
                        <div className="ritual-suggestions">
                            {NAME_SUGGESTIONS.map(s => (
                                <button
                                    key={s}
                                    className="ritual-suggestion"
                                    onClick={() => setReplicaName(s)}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                        <button
                            className="ritual-cta"
                            onClick={handleNext}
                            disabled={!replicaName.trim()}
                        >
                            {t('ritual.next')}
                        </button>
                    </div>
                );

            // ═══ SCREEN 3 — LA GRAINE ═══
            case 2:
                return (
                    <div className={stepClass} key="seed">
                        <div className={`ritual-silhouette ${getSilhouetteClass()}`}>
                            <canvas ref={silhouetteCanvasRef} />
                        </div>
                        <h1 className="ritual-title">
                            {t('ritual.seed_title')}
                        </h1>
                        <p className="ritual-subtitle">
                            {t('ritual.seed_subtitle')}
                        </p>
                        <textarea
                            className="ritual-textarea"
                            placeholder={t('ritual.seed_placeholder')}
                            value={seedPhrase}
                            onChange={(e) => setSeedPhrase(e.target.value)}
                            maxLength={280}
                        />
                        <button
                            className="ritual-cta"
                            onClick={handleNext}
                            disabled={!seedPhrase.trim()}
                        >
                            {t('ritual.next')}
                        </button>
                    </div>
                );

            // ═══ SCREEN 4 — LA CONNEXION ═══
            case 3:
                return (
                    <div className={stepClass} key="voice">
                        <div className={`ritual-silhouette ${getSilhouetteClass()}`} style={{ position: 'relative' }}>
                            <canvas ref={silhouetteCanvasRef} />
                            <div className="ritual-heartbeat" />
                        </div>
                        <h1 className="ritual-title">
                            {t('ritual.voice_title')}
                        </h1>
                        <p className="ritual-subtitle">
                            {t('ritual.voice_subtitle')}
                        </p>
                        <div className="ritual-voice-options">
                            {VOICE_TONE_IDS.map(id => (
                                <button
                                    key={id}
                                    className={`ritual-voice-option ${voiceTone === id ? 'ritual-voice-option--active' : ''}`}
                                    onClick={() => setVoiceTone(id)}
                                >
                                    <span>{t(`ritual.voice_${id}`)}</span>
                                </button>
                            ))}
                        </div>
                        <button
                            className="ritual-cta"
                            onClick={() => {
                                goToStep(4);
                                setTimeout(triggerBirth, 800);
                            }}
                        >
                            {t('ritual.give_life')}
                        </button>
                    </div>
                );

            // ═══ SCREEN 5 — LA NAISSANCE ═══
            case 4:
                return (
                    <div className={stepClass} key="birth">
                        {!isBorn ? (
                            <>
                                <div className={`ritual-silhouette ${getSilhouetteClass()}`}>
                                    <canvas ref={silhouetteCanvasRef} />
                                </div>
                                {isCreating && (
                                    <p className="ritual-subtitle" style={{ marginBottom: '1rem' }}>
                                        <span className="ritual-spinner" style={{ marginRight: '0.75rem', verticalAlign: 'middle' }} />
                                        {t('ritual.creating')}
                                    </p>
                                )}
                                {error && (
                                    <p style={{ color: '#FF453A', fontSize: '0.9rem', marginTop: '1rem' }}>
                                        {error}
                                        <br />
                                        <button
                                            className="ritual-cta"
                                            onClick={triggerBirth}
                                            style={{ marginTop: '1rem' }}
                                        >
                                            {t('ritual.retry')}
                                        </button>
                                    </p>
                                )}
                            </>
                        ) : (
                            <>
                                <div className="ritual-silhouette ritual-silhouette--born">
                                    <canvas ref={silhouetteCanvasRef} />
                                </div>
                                <div className="ritual-born-info">
                                    <h1 className="ritual-title" style={{ marginBottom: '0.5rem' }}>
                                        {t('ritual.born_title')}
                                    </h1>
                                    <div className="ritual-born-name">{replicaName}</div>
                                    <p className="ritual-born-phrase">« {seedPhrase} »</p>
                                    <p className="ritual-born-voice">
                                        {t('ritual.born_voice_label')} {t(`ritual.voice_${voiceTone}`)}
                                    </p>
                                </div>
                                <button className="ritual-cta ritual-cta--primary" onClick={handleFinish}>
                                    {t('ritual.finish_cta')}
                                </button>
                            </>
                        )}
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="ritual-container">
            {/* Background gradient */}
            <div className={`ritual-bg ${getBgClass()}`} />

            {/* Particle layer */}
            <canvas ref={particleCanvasRef} className="ritual-particles" />

            {/* Birth flash overlay */}
            {showBirthFlash && <div className="ritual-birth-flash" />}

            {/* Close button */}
            {onCancel && (
                <button className="ritual-close" onClick={onCancel} aria-label={t('ritual.close_label')}>
                    <X size={18} />
                </button>
            )}

            {/* Content */}
            <div className="ritual-content">
                {renderStep()}
            </div>

            {/* Progress dots */}
            <div className="ritual-progress">
                {[0, 1, 2, 3, 4].map(i => (
                    <button
                        key={i}
                        className={`ritual-dot ${i === currentStep ? 'ritual-dot--active' :
                            i < currentStep ? 'ritual-dot--completed' : ''
                            }`}
                        onClick={() => i < currentStep && goToStep(i)}
                        aria-label={t('ritual.step_label', { num: i + 1 })}
                    />
                ))}
            </div>
        </div>
    );
};

export default ReplicaCreationRitual;
